import React, {useState} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {useCredentials} from '../../lib/user';
import {getErrorMessage} from '../../lib/helpers';
import Button from '../../components/Button';
import Icon from '../../components/Icon';

import {
  getReuseModesFor,
  getReuseMode,
  getReuseModeLabel,
  receivesSharedAnswers,
  canBeSharedAnswerSource,
  canReceiveSharedAnswers,
  getMeasurementUnit
} from './shared';
import Checkbox from './Checkbox';
import SubConfiguration from './SubConfiguration';
import SharedAnswerSourcePicker from './SharedAnswerSourcePicker';
import OfferedAnswers from './OfferedAnswers';

// Shared answer settings for a question in the form builder. They are edited like the rest of the question card and
// saved with the form.
//  - onChange receives the changes to apply (see withSharedAnswerSettings). They are applied to the question as it is
//    when they arrive, so a change made after a request or a confirmation never overwrites newer edits.
//  - onSourceRemoved is told when this question stops being a source, so questions on this form drop it too.
//  - onConfirm asks the card to confirm an action before it runs.
//  - getSourceForms returns the forms the source picker can offer.
//  - existsOnServer is true when the question was loaded from a saved form, so other forms may already link to it.
//  - canRemoveOfferedAnswers is true when the user may stop offering a shared answer, which applies on every form.
function SharedAnswersSettings({question, existsOnServer, canRemoveOfferedAnswers, onChange, onSourceRemoved, onConfirm, getSourceForms}) {
  const credentials = useCredentials();
  const reuseMode = getReuseMode(question);
  const receiving = receivesSharedAnswers(question);
  const showSource = canBeSharedAnswerSource(question) || !!reuseMode;
  const showReceiving = canReceiveSharedAnswers(question) || receiving;
  const sources = question.sharedAnswerSources;
  // Until a receiving question's sources have loaded, any change to it could be saved over links it already has.
  const sourcesUnavailable = receiving && !Array.isArray(sources);
  const reuseModes = getReuseModesFor(question);
  const inventorySource = question.type === 'INVENTORY_RECEIVING';
  const inventoryUsage = question.type === 'INVENTORY_USAGE';
  // A usage question only links to receiving questions with the same kind of unit, so it needs its own unit first.
  const needsUnit = inventoryUsage && !getMeasurementUnit(question);

  const [checkingLinks, setCheckingLinks] = useState(false);
  const [pickerForms, setPickerForms] = useState(null);

  const update = changes => onChange(changes);

  const stopSharing = () => {
    update({reuseMode: null});
    onSourceRemoved(question.id);
  };

  const handleSourceToggle = async e => {
    if (e.target.checked) {
      update({reuseMode: reuseModes[0].value});
      return;
    }

    if (!existsOnServer) {
      stopSharing();
      return;
    }

    // Questions on other forms may read from this one. Count them so the confirmation says what the save will do.
    setCheckingLinks(true);
    const links = await api.getSourceQuestionLinks(question.id, credentials)
      .catch(async error => {
        NotificationManager.error(await getErrorMessage(error, 'Unable to check which questions use this one.'));
        return null;
      });
    setCheckingLinks(false);

    const otherFormLinks = links ? links.length : null;

    if (otherFormLinks === 0) {
      stopSharing();
      return;
    }

    onConfirm({
      title: 'Stop sharing these answers?',
      description: otherFormLinks === null
        ? 'Any questions linked to this one will be unlinked when you save the form.'
        : `${plural(otherFormLinks, 'question')} linked to this one will be unlinked when you save the form.`,
      ok: 'Stop Sharing',
      onOk: stopSharing
    });
  };

  const handleReceivingToggle = e => {
    if (e.target.checked) {
      update({receivesSharedAnswers: true, sources: sources || []});
      return;
    }

    const turnOff = () => update({receivesSharedAnswers: false, sources: []});

    if (!sources || sources.length === 0) {
      turnOff();
      return;
    }

    onConfirm({
      title: 'Stop receiving shared answers?',
      description: question.type === 'INVENTORY_USAGE'
        ? `${plural(sources.length, 'linked source')} will be removed when you save the form, and this question will no longer offer received lots.`
        : `${plural(sources.length, 'linked source')} will be removed when you save the form, and this question will only offer its own options.`,
      ok: 'Turn Off',
      onOk: turnOff
    });
  };

  const handleLinkSource = (source, form) => {
    update({addSource: {
      sourceQuestionId: source.id,
      sourceQuestionText: source.text,
      sourceTemplateName: form.name,
      reuseMode: getReuseMode(source)
    }});
    setPickerForms(null);
  };

  const handleRemoveSource = source => {
    update({removeSourceId: source.sourceQuestionId});
  };

  if (!showSource && !showReceiving) {
    return null;
  }

  const hasDefaultAnswer = question.defaultAnswers.length > 0 || !!question.lockDefault;

  return (
    <>
      {showSource && (
        <Setting>
          <Checkbox>
            <input
              type="checkbox"
              checked={!!reuseMode}
              disabled={checkingLinks || sourcesUnavailable}
              onChange={handleSourceToggle}
            />
            &nbsp;
            Use as a source for other forms
          </Checkbox>
          {reuseMode && (
            <SubConfiguration role="radiogroup" aria-label="How shared answers can be used">
              {reuseModes.map(mode => (
                <SubConfiguration.Item key={mode.value}>
                  <RadioLabel $unavailable={!!mode.unavailable}>
                    <input
                      type="radio"
                      name={`reuse-mode-${question.id}`}
                      checked={reuseMode === mode.value}
                      disabled={sourcesUnavailable || !!mode.unavailable}
                      onChange={() => update({reuseMode: mode.value})}
                    />
                    <span>
                      {mode.label}
                      <Description>{mode.description}</Description>
                      {mode.unavailable && <Description>{mode.unavailable}</Description>}
                    </span>
                  </RadioLabel>
                </SubConfiguration.Item>
              ))}
            </SubConfiguration>
          )}
          {!reuseMode && (
            <Help>
              {inventorySource
                ? 'Lets inventory usage questions on other forms pick the lots received here.'
                : 'Lets questions on other forms offer the answers submitted here as options.'}
            </Help>
          )}
        </Setting>
      )}
      {showReceiving && (
        <Setting>
          <Checkbox>
            <input
              type="checkbox"
              checked={receiving}
              disabled={sourcesUnavailable || (!receiving && hasDefaultAnswer)}
              onChange={handleReceivingToggle}
            />
            &nbsp;
            {inventoryUsage ? 'Draw from receiving questions' : 'Receive shared answers'}
          </Checkbox>
          <Help>
            {!receiving && hasDefaultAnswer
              ? 'Remove the default answer to receive shared answers.'
              : inventoryUsage
                ? 'Offers the lots received on linked inventory receiving questions as items to pick.'
                : 'Offers answers from linked source questions as extra options.'}
          </Help>
          {receiving && (
            <>
              <SubConfiguration aria-live="polite">
                {sources === undefined && (
                  <SubConfiguration.Item>Loading linked sources...</SubConfiguration.Item>
                )}
                {sourcesUnavailable && (
                  <SubConfiguration.Item>
                    The linked sources could not be loaded, so they can't be changed right now. Reload the page to try
                    again.
                  </SubConfiguration.Item>
                )}
                {sources && sources.length === 0 && (
                  <SubConfiguration.Item>No sources linked yet.</SubConfiguration.Item>
                )}
                {(sources || []).map(source => (
                  <SubConfiguration.Item key={source.sourceQuestionId}>
                    <LinkRow>
                      <LinkText>
                        {source.sourceQuestionText}
                        <Description>{source.sourceTemplateName}</Description>
                      </LinkText>
                      <ModeTag>{getReuseModeLabel(source.reuseMode)}</ModeTag>
                      <Button
                        tertiary
                        title="Remove source"
                        aria-label={`Remove ${source.sourceQuestionText} as a source`}
                        onClick={() => handleRemoveSource(source)}>
                        <Icon name="trash-2" />
                      </Button>
                    </LinkRow>
                  </SubConfiguration.Item>
                ))}
              </SubConfiguration>
              <Button
                tertiary
                disabled={!sources || needsUnit}
                onClick={() => setPickerForms(getSourceForms())}>
                <Icon name="plus" /> Add a Source
              </Button>
              {needsUnit && (
                <Help>Choose a unit before adding a source.</Help>
              )}
              {existsOnServer && canRemoveOfferedAnswers && (
                <OfferedAnswers question={question} onConfirm={onConfirm} />
              )}
            </>
          )}
          {pickerForms && (
            <SharedAnswerSourcePicker
              forms={pickerForms}
              question={question}
              linkedSourceIds={(sources || []).map(source => source.sourceQuestionId)}
              onLink={handleLinkSource}
              onClose={() => setPickerForms(null)}
            />
          )}
        </Setting>
      )}
    </>
  );
}

function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

const Setting = styled.div`
  > * + * {
    margin-top: 0.5rem;
  }
`;

const Help = styled.small`
  display: block;
  font-size: 0.875rem;
  color: #747474;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  white-space: normal;
  cursor: pointer;

  ${props => props.$unavailable && `
    opacity: 0.6;
    cursor: default;
  `}
`;

const Description = styled.span`
  display: block;
  font-size: 0.875rem;
  color: #747474;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  white-space: normal;
`;

const LinkText = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ModeTag = styled.span`
  font-size: 0.75rem;
  color: #555;
  background: #fff;
  border: 0.0625rem solid #e0e5ea;
  border-radius: 0.25rem;
  padding: 0.125rem 0.5rem;
  white-space: nowrap;
`;

export default SharedAnswersSettings;
